import {
  ReviewDetailLevel,
  useTrainingSimulatorReview,
} from "@/lib/api/training-simulator/training-simulator-review";
import { Loader2 } from "lucide-react";
import {
  PostHogEvents,
  useTrackEvent,
  useTrackPage,
} from "@/lib/posthog-events";
import { TrainingReviewFeedback } from "./training-review-feedback";
import { TrainingReviewNavigation } from "./training-review-navigation";
import { TrainingReviewTopBar } from "./training-review-topbar";
import { useMemo, useState } from "react";
import { TrainingSessionWorkspace } from "../session/training-session-workspace";
import type { TrainingAttemptThread } from "@/lib/api/training-simulator/training-simulator";

export function TrainingReviewDetails({ progressId }: { progressId: string }) {
  useTrackPage(PostHogEvents.PAGE_TRAINING_REVIEW_DETAIL, {
    progress_id: progressId,
  });
  const { track } = useTrackEvent();
  const { data, isLoading, isError } = useTrainingSimulatorReview(progressId);

  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);

  const selectedModule =
    data?.modules.find((module) => module.id === selectedModuleId) ??
    data?.modules[0];

  const selectedLevel =
    selectedModule?.levels.find((level) => level.id === selectedLevelId) ??
    selectedModule?.levels[0];

  const reviewThreads = useMemo(
    () =>
      selectedLevel?.threads.map(({ thread, teen }) =>
        withReviewTeen(thread, teen),
      ) ?? [],
    [selectedLevel],
  );

  const reviewMessageGroups = useMemo(
    () =>
      selectedLevel?.threads.map(({ thread, teen, messages, events }) => ({
        thread: withReviewTeen(thread, teen),
        messages,
        events,
      })) ?? [],
    [selectedLevel],
  );

  if (isLoading || !selectedModule || !selectedLevel) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3">
        <Loader2 className="size-10 animate-spin text-text-secondary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3">
        <p className="text-body-1 text-text-secondary">
          Something went wrong. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surfaces-z3">
      <TrainingReviewTopBar
        module={selectedModule}
        mentor={data.mentor}
        joinedAt={data.joined_at}
        level={selectedLevel}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <TrainingReviewNavigation
          modules={data.modules}
          selectedLevel={selectedLevel}
          selectedModule={selectedModule}
          onSelectModule={(module) => {
            track(PostHogEvents.SIMULATOR_LM_MODULE_SELECT, {
              module_id: module.id,
            });
            setSelectedModuleId(module.id);
            setSelectedLevelId(module.levels[0]?.id ?? null);
          }}
          onSelectLevel={(level) => {
            track(PostHogEvents.SIMULATOR_LM_LEVEL_SELECT, {
              level_id: level.id,
            });
            setSelectedLevelId(level.id);
          }}
        />

        {selectedLevel.latestAttempt && (
          <div className="mb-6 flex h-[calc(100vh-196px)] min-h-[420px]">
            <TrainingSessionWorkspace
              attemptId={selectedLevel.latestAttempt.id}
              threads={reviewThreads}
              messageGroups={reviewMessageGroups}
              defaultThreadId={selectedLevel.latestAttempt.current_thread_id}
              isInProgress={false}
              readOnly
            />
          </div>
        )}

        <TrainingReviewFeedback
          review={data.review}
          progressId={progressId}
          isApproved={data.status === "approved"}
        />
      </div>
    </div>
  );
}

function withReviewTeen(
  thread: TrainingAttemptThread,
  teen: ReviewDetailLevel["threads"][number]["teen"],
): TrainingAttemptThread {
  if (thread.level_teen || !teen) return thread;

  return {
    ...thread,
    level_teen: {
      ...teen,
      position: 0,
      conversation_bucket: thread.conversation_bucket ?? "new",
    },
  };
}
